import { create } from 'zustand';
import type { MergePlan } from '@/previs/mergePlan';

/**
 * The one wire code that is NOT an error: the user dismissed the native file picker.
 * It returns the import UI to its pre-request state and renders nothing — no banner, no
 * toast. Kept as a named constant because that "silently" is a real product rule, easy to
 * lose to a future refactor that treats every code the same.
 */
export const REIMPORT_PICKER_CANCELLED = 'picker-cancelled';

/**
 * The second non-error code: Unity tore the picker session down from its own side (panel exit,
 * webview cleanup, a genuine project boot). It exists so the web has a DETERMINISTIC release for
 * the picker blocker — those teardown paths used to send nothing at all, leaving the overlay up
 * until its 120 s failsafe expired.
 *
 * Treated EXACTLY like picker-cancelled: silent reset, no banner. The user did not do anything
 * wrong and, on every path that emits it, whatever they were looking at is already going away.
 */
export const REIMPORT_SESSION_TORN_DOWN = 'session-torn-down';

/** The codes that return the import UI to its pre-request state and render nothing. Anything
 *  NOT in here becomes a visible error, including codes this build has never heard of. */
const SILENT_RESET_CODES: ReadonlySet<string> = new Set([
  REIMPORT_PICKER_CANCELLED,
  REIMPORT_SESSION_TORN_DOWN,
]);

/**
 * Last-resort release of `importRequestPending` (and with it the picker blocker bound to it).
 *
 * Every REAL terminal path already clears the flag: startSession, reportImportError (including the
 * two silent codes), endSession, endImportRequest. This exists for the hosts that answer NOTHING —
 * an older Unity build with no `reimportError` verb, or a teardown that raced the webview's own
 * destruction. Two minutes, not the 1500 ms the click-debounce used to double as: that value
 * released the busy state mid-pick, which is exactly the hole the blocker closes. A wait this long
 * is only ever reached by a host that is never going to answer, and the blocker's 10 s Dismiss
 * means the user never has to sit it out.
 */
export const IMPORT_PICKER_FAILSAFE_MS = 120_000;

/**
 * THE TIMER LIVES WITH THE FLAG IT RELEASES, and that is the point of it being here rather than in
 * ScreenplayManager. The flag is a module global that locks the whole shell (segments disabled,
 * back arrow inert, hardware Escape parked), so a release owned by a component could — and did —
 * die with an unmount while the lock stayed on: no blocker on screen, no Dismiss, and no timer left
 * to fire. Module scope, not component state, so the guard and its escape hatch have one lifetime.
 */
let pickerFailsafeTimer: ReturnType<typeof setTimeout> | null = null;

function disarmPickerFailsafe(): void {
  if (pickerFailsafeTimer === null) return;
  clearTimeout(pickerFailsafeTimer);
  pickerFailsafeTimer = null;
}

/** A reimport failure as reported by Unity's `reimportError` verb.
 *  `code` is deliberately a plain string, not a union: a code this build has never heard of
 *  must still reach the user as a generic message carrying the raw code (see the banner). */
export interface ReimportError {
  code: string;
  /** Optional secondary text — may be a filename or raw parser output. Plain text ONLY;
   *  never interpolated into an i18n template. */
  detail?: string;
}

// The store holds raw Unity-side JSON payloads (pre-normalization).
// Task 13's ImportPreview normalises them into NormalizedScene[] via
// parseSlugline / normalizeBaselineData before handing them to
// classifyThreeWay / buildMergePlan. Typing them as `unknown` keeps that
// contract honest and forces the consumer to normalise explicitly.
export interface ScreenplayImportState {
  active: boolean;
  baselineHash: string | null;
  baseline: unknown | null;
  current: unknown | null;
  parsedScreenplay: unknown | null;
  mergePlan: MergePlan | null;
  applyStatus: 'idle' | 'submitting' | 'success' | 'error';
  /** User-forced scene-match overrides. Key = current-side scene id
   *  (e.g. "cur-10"). Value = incoming-side scene id (e.g. "inc-2") OR
   *  null to break an existing match. Consumed by applyForcedMatches
   *  after classifyThreeWay. */
  forcedMatches: Record<string, string | null>;
  /** User-driven incoming scene reorder overrides. Key = incoming scene id
   *  (e.g. "inc-3"). Value = the new 0-indexed script order the user
   *  dropped the scene at. Applied before classifyThreeWay runs so the
   *  3-way matcher sees the user-intended order. Cleared on endSession
   *  and startSession. */
  userIncomingOrder: Record<string, number>;
  /** True from the moment the web asks Unity to open the picker until Unity answers —
   *  with a session (`startReimport`), an error, or a picker cancellation. Owns the button's
   *  busy state so a `reimportError` can release it the instant it lands. */
  importRequestPending: boolean;
  /** The last failure to show the user, or null. Survives an apply failure (the session stays
   *  active, so the banner has somewhere to render) and is cleared by start/endSession. */
  lastError: ReimportError | null;

  /**
   * A parked "leave the review" action, awaiting the user's answer to the cancel-confirm.
   * Null when no confirm is showing. Set only through requestSessionExit.
   */
  exitRequest: (() => void) | null;
  /**
   * THE single entry point for every route out of an active import review — Unity's
   * hardware-Escape (`window.__requestBack`) and Import Review's own Cancel both call this one
   * action, so a future edit cannot fix one path and forget another. (The shell's top-bar back
   * arrow was a third caller until it was removed on 2026-08-09; nothing here changed with it.)
   *
   * Returns TRUE when it PARKED the caller's action behind a confirmation — meaning "handled,
   * stop what you were doing", not "the session ended". Same contract, deliberately, as
   * uiStore.closeOpenWorkspace()'s generation-in-flight branch, which the __requestBack route
   * already depends on. Returns FALSE when there is no session, and the caller should just proceed.
   *
   * Also returns TRUE — parking nothing — while an apply is in flight. See below.
   */
  requestSessionExit: (afterExit: () => void) => boolean;
  /** User said "stay" — drop the parked action and keep reviewing. */
  dismissSessionExit: () => void;

  /** Mark the picker request in flight, and arm the failsafe that releases it if the host never
   *  answers. Any previous error is stale from this point. */
  beginImportRequest: () => void;
  /** Release the busy state without reporting anything — a caller gave up waiting (the blocker's
   *  Dismiss, an unmount, the failsafe). Deliberately separate from reportImportError: nothing
   *  failed, so nothing shows. */
  endImportRequest: () => void;
  /** Apply an inbound `reimportError`. Ends the pending state either way; only a code OUTSIDE
   *  SILENT_RESET_CODES (picker-cancelled, session-torn-down) becomes a visible error. */
  reportImportError: (code: string, detail?: string) => void;
  clearImportError: () => void;

  startSession: (payload: {
    baselineHash: string;
    baseline: unknown;
    current: unknown;
    parsedScreenplay: unknown;
  }) => void;
  setMergePlan: (plan: MergePlan) => void;
  endSession: () => void;
  setApplyStatus: (status: 'idle' | 'submitting' | 'success' | 'error') => void;
  setForcedMatch: (leftId: string, rightId: string | null) => void;
  clearForcedMatch: (leftId: string) => void;
  setIncomingOrder: (sceneId: string, newScriptOrder: number) => void;
  clearIncomingOrder: () => void;
}

export const useScreenplayImportStore = create<ScreenplayImportState>((set, get) => ({
  active: false,
  baselineHash: null,
  baseline: null,
  current: null,
  parsedScreenplay: null,
  mergePlan: null,
  applyStatus: 'idle',
  forcedMatches: {},
  userIncomingOrder: {},
  importRequestPending: false,
  lastError: null,
  exitRequest: null,

  requestSessionExit: (afterExit) => {
    if (!get().active) return false;
    // An apply is on the wire. The confirm's own words — "the project is unchanged" — stop being
    // true the instant the merge is sent, and Unity is committing it right now, so there is no
    // honest question left to ask. Return TRUE rather than false: false sends the caller on to
    // its own action, and the back arrow's is closing the project out from under a write. This is
    // the same freeze Apply already applies to itself (ImportPreview's `applyStatus` guard),
    // extended to the exits. It lifts on the reply — failure re-opens every route, success ends
    // the session — so the only way to sit here is Unity never answering at all.
    if (get().applyStatus === 'submitting') return true;
    // FIRST request wins: a parked continuation is never silently swapped, or the user would
    // answer the question the back arrow asked and get the other route's action. The one caller
    // that can genuinely fire twice — Unity's hardware Escape, which reaches `__requestBack`
    // directly as an evaluateJavaScript call and so is invisible to the dialog's own key handler
    // — dismisses the confirm at its call site instead of re-parking here, matching what a second
    // web Escape does. This guard is what makes that safe to rely on.
    if (get().exitRequest) return true;
    set({ exitRequest: afterExit });
    return true;
  },
  dismissSessionExit: () => set({ exitRequest: null }),

  beginImportRequest: () => {
    // Re-arm from scratch: a second request inside an earlier failsafe window would otherwise
    // inherit the FIRST request's expiry and have its pending flag cleared out from under it.
    disarmPickerFailsafe();
    pickerFailsafeTimer = setTimeout(() => {
      pickerFailsafeTimer = null;
      // Re-check rather than assume. Nothing else can leave this timer armed (every terminal
      // action disarms), but a release that lands in the same tick should win.
      if (get().importRequestPending) get().endImportRequest();
    }, IMPORT_PICKER_FAILSAFE_MS);
    set({ importRequestPending: true, lastError: null });
  },
  endImportRequest: () => {
    disarmPickerFailsafe();
    set({ importRequestPending: false });
  },
  reportImportError: (code, detail) => {
    disarmPickerFailsafe();
    if (!SILENT_RESET_CODES.has(code)) {
      set({ importRequestPending: false, lastError: { code, detail } });
      return;
    }
    // Not an error: the user closed the picker, or Unity tore the session down from its own side.
    // Release the busy affordance and show nothing — a banner here would read as a failure the
    // user did not cause.
    //
    // ...but DO NOT WIPE A LIVE BANNER on the way past. `session-torn-down` can arrive while an
    // apply-failure banner is on screen, and on the paths that send exist for — the ones where the
    // page SURVIVES the teardown — clearing it would take away the only explanation the user had
    // for what just failed, with nothing appearing in its place. applyStatus === 'error' is
    // exactly "ImportPreview is rendering a banner right now".
    if (get().applyStatus === 'error') {
      set({ importRequestPending: false });
      return;
    }
    set({ importRequestPending: false, lastError: null });
  },
  clearImportError: () => set({ lastError: null }),

  startSession: ({ baselineHash, baseline, current, parsedScreenplay }) => {
    disarmPickerFailsafe();
    set({
      active: true,
      baselineHash,
      baseline,
      current,
      parsedScreenplay,
      mergePlan: null,
      applyStatus: 'idle',
      forcedMatches: {},
      userIncomingOrder: {},
      // The request landed, and any earlier failure is now history.
      importRequestPending: false,
      lastError: null,
      // A confirm that was open when the session turned over has nothing left to guard.
      exitRequest: null,
    });
  },
  setMergePlan: (plan) => set({ mergePlan: plan }),
  endSession: () => {
    disarmPickerFailsafe();
    set({
      active: false, baselineHash: null, baseline: null, current: null,
      parsedScreenplay: null, mergePlan: null, applyStatus: 'idle',
      forcedMatches: {},
      userIncomingOrder: {},
      importRequestPending: false,
      lastError: null,
      // A confirm that was open when the session turned over has nothing left to guard.
      exitRequest: null,
    });
  },
  setApplyStatus: (status) => set({ applyStatus: status }),
  setForcedMatch: (leftId, rightId) => set(s => ({
    forcedMatches: { ...s.forcedMatches, [leftId]: rightId },
  })),
  clearForcedMatch: (leftId) => set(s => {
    const rest = { ...s.forcedMatches };
    delete rest[leftId];
    return { forcedMatches: rest };
  }),
  setIncomingOrder: (sceneId, newScriptOrder) => set(s => ({
    userIncomingOrder: { ...s.userIncomingOrder, [sceneId]: newScriptOrder },
  })),
  clearIncomingOrder: () => set({ userIncomingOrder: {} }),
}));
